/**
 * Поп-ап «что нового» во встроенном приложении CRM (#946).
 *
 * Отдельная точка подключения, потому что во встроенном контексте нет Clerk:
 * `/users/me` там недостижим, состояние приходит из `/api/v1/embedded/whats-new`
 * по cookie-сессии. Сессия может быть ещё не связана с пользователем Aelo —
 * тогда `canRecord: false`, и поп-ап не показывается: отметку писать некуда, и
 * он возвращался бы при каждом заходе.
 *
 * Диалог живёт внутри нашего iframe, поэтому интерфейс портала он не
 * перекрывает.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EmbeddedWhatsNewStateSchema } from '@aica/api-types';
import { useEmbeddedAuthFetch } from '@/hooks/useEmbeddedAuthFetch';
import { logError } from '@/lib/logger';
import { WhatsNewGate } from './WhatsNewGate';

const QUERY_KEY = ['embedded', 'whats-new'] as const;

export function EmbeddedWhatsNew() {
  const { authenticatedFetch } = useEmbeddedAuthFetch();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: QUERY_KEY,
    // Ответ проверяется схемой, а не берётся на веру: форма — общий контракт
    // (`@aica/api-types`), и `tier` из него решает, скажем ли мы клиенту, что
    // возможность ему недоступна. Разошедшийся воркер должен привести к
    // отсутствию поп-апа, а не к ложной пометке.
    queryFn: async () => {
      const raw = await authenticatedFetch<unknown>('/api/v1/embedded/whats-new');
      const parsed = EmbeddedWhatsNewStateSchema.safeParse(raw);
      if (!parsed.success) {
        logError('Invalid embedded whats-new response', {
          component: 'EmbeddedWhatsNew',
          issues: parsed.error.issues.map((i) => i.path.join('.')).join(','),
        });
        return null;
      }
      return parsed.data;
    },
    // Релиз за время сессии не меняется, а поп-ап показывается один раз —
    // перечитывать это состояние незачем.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleSeen = useCallback(
    async (version: string) => {
      await authenticatedFetch('/api/v1/embedded/whats-new/seen', {
        method: 'POST',
        body: JSON.stringify({ version }),
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    [authenticatedFetch, queryClient]
  );

  // Пока состояние не пришло, показывать нечего: поп-ап без него мог бы
  // появиться повторно у того, кто его уже закрыл.
  if (!data) return null;

  return (
    <WhatsNewGate
      lastSeenRelease={data.lastSeenRelease}
      userCreatedAt={data.userCreatedAt}
      canRecord={data.canRecord}
      tier={data.tier}
      canViewPlans={data.canViewPlans ?? false}
      onSeen={handleSeen}
    />
  );
}
